// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { ApiException } from '@digitalasset/validator-scan-proxy-api-ts-client';
import { useAccounts } from '@hooks/dapp/useAccounts';
import { useConnect } from '@hooks/dapp/useConnect';
import { useConfig } from '@hooks/other/useConfig';
import { useSnackbar } from 'notistack';
import { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react';
import { hasAuthParams, useAuth } from 'react-oidc-context';
import { useLocation, useNavigate } from 'react-router-dom';
import { decodeToken, generateToken } from '../utils/auth';
import { useIdpInfo } from './IdpContext';
import { LoggedInUser, UserContext } from './UserContext';

export const UserProvider = ({ children }: PropsWithChildren) => {
  const [loggedInUser, setLoggedInUser] = useState<LoggedInUser>();
  const oidcAuth = useAuth();
  const { idpInfo, unsetIdpInfo, setIdpError } = useIdpInfo();
  const nav = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { enableDappLogin } = useConfig().app;
  const { connect: dappConnect, disconnect, status } = useConnect();
  const accounts = useAccounts(status);
  const primaryAccount = accounts?.find((account) => account.primary);

  useEffect(() => {
    if (enableDappLogin && accounts && status?.isConnected) {
      const hasPrimary = accounts.length > 0 && accounts.find((account) => account.primary);

      if (!hasPrimary) {
        enqueueSnackbar('No primary party set. You may need to synchronize wallets.', {
          variant: 'info',
        });
      }
    }
  }, [enableDappLogin, accounts, status, enqueueSnackbar]);

  useEffect(() => {
    if (enableDappLogin) {
      if (status?.session) {
        if (primaryAccount) {
          setLoggedInUser({
            userId: status.session.userId,
            userAccessToken: status.session.accessToken,
            primaryParty: primaryAccount.partyId,
            isConnectedViaDapp: true,
          });
        }
      } else {
        setLoggedInUser(undefined);
      }
    }
  }, [enableDappLogin, primaryAccount, status]);

  // Redirect after login
  useEffect(() => {
    if (loggedInUser && location.pathname.includes('/login')) {
      nav('/');
    }
  }, [loggedInUser, location, nav]);

  useEffect(() => {
    if (idpInfo?.algorithm === 'rs-256' && oidcAuth && oidcAuth.isAuthenticated && oidcAuth.user) {
      const userId = decodeToken(oidcAuth.user.access_token).sub!;
      setLoggedInUser({
        userId,
        userAccessToken: oidcAuth.user.access_token,
      });
    }
  }, [idpInfo, oidcAuth]);

  const connect = useCallback(async () => {
    try {
      await dappConnect();
    } catch (e) {
      enqueueSnackbar('Error connecting', { variant: 'error' });
    }
  }, [dappConnect, enqueueSnackbar]);

  const login = useCallback(
    async (userId?: string) => {
      if (idpInfo?.algorithm === 'hs-256-unsafe') {
        if (!userId) {
          throw new Error("User ID required for 'HS256' login");
        }
        const userAccessToken = await generateToken(
          userId,
          idpInfo.secret,
          idpInfo.audience,
          idpInfo.scope,
        );
        setLoggedInUser({ userId, userAccessToken });
      } else if (
        idpInfo?.algorithm === 'rs-256' &&
        !hasAuthParams() &&
        !oidcAuth.isAuthenticated &&
        !oidcAuth.activeNavigator &&
        !oidcAuth.isLoading
      ) {
        console.debug('User is unauthenticated. Signing in.');

        await oidcAuth.signinRedirect();
        if (oidcAuth.error) {
          unsetIdpInfo();
          setIdpError(
            'There is an error with the identity provider configuration, please try again or contact your administrator.',
          );
        }
      } else {
        console.debug('Authentication process is still executing.');
      }
    },
    [idpInfo, setIdpError, unsetIdpInfo, oidcAuth],
  );

  const logout = useCallback(async () => {
    if (enableDappLogin) {
      await disconnect();
    }
    setLoggedInUser(undefined);

    if (idpInfo?.algorithm === 'rs-256' && oidcAuth) {
      unsetIdpInfo();
      await oidcAuth.signoutRedirect({
        id_token_hint: oidcAuth.user?.id_token,
        post_logout_redirect_uri: window.location.origin,
      });
    }
  }, [idpInfo, oidcAuth, unsetIdpInfo, disconnect, enableDappLogin]);

  const isApiException = (error: unknown): error is ApiException<unknown> => {
    return error !== null && typeof error === 'object' && 'code' in error;
  };

  const signoutOnUnauthorized = useCallback(
    (err: unknown) => {
      if (isApiException(err) && err.code === 401) {
        logout().catch(() => console.debug('There was an issue redirecting the user to logout.'));
      }
    },
    [logout],
  );

  const isSelfSignedToken = useCallback(() => idpInfo?.algorithm === 'hs-256-unsafe', [idpInfo]);

  const userState = useMemo(
    () => ({
      dappStatus: status,
      loggedInUser,
      login,
      logout,
      isSelfSignedToken,
      signoutOnUnauthorized,
      connect,
      setLoggedInUser,
    }),
    [connect, status, loggedInUser, login, logout, isSelfSignedToken, signoutOnUnauthorized],
  );

  return <UserContext.Provider value={userState}>{children}</UserContext.Provider>;
};
